import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Chip,
  Container,
  GlobalStyles,
  Grid,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  GridActionsCellItem,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  esES,
} from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import { PageHeaderCard } from "src/general_components/PageHeaderCard";
import {
  StyledDataGrid,
  estiloGrilla,
  globalEstilos,
} from "src/general_components/FunctionsListTablePaginated";
import { FormOcrPlantilla } from "../components/FormOcrPlantilla";
import { ocrPlantillasListar, ocrPlantillasEliminar } from "../helpers/OcrData";

const TIPO_COLOR = {
  extraccion: "primary",
  validacion: "secondary",
};

const BTN = {
  color: '#FFFFFF',
  fontFamily: 'Roboto, sans-serif',
  background: '#0A395F',
  margin: '4px',
  minWidth: '120px',
  width: '120px',
  fontSize: '11px',
  height: '30px',
};

function CustomToolbar({ onNueva }) {
  return (
    <GridToolbarContainer style={{ padding: 10, display: 'flex', justifyContent: 'space-between' }}>
      <Grid>
        <GridToolbarColumnsButton style={BTN} />
        <GridToolbarFilterButton style={BTN} />
        <GridToolbarDensitySelector style={BTN} />
        <GridToolbarExport style={BTN} />
      </Grid>
      <Button
        startIcon={<AddCircleIcon />}
        onClick={onNueva}
        style={{ ...BTN, minWidth: 160, width: 160 }}
      >
        Nueva plantilla
      </Button>
    </GridToolbarContainer>
  );
}

export function OcrPlantillas() {
  const theme = useTheme();
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState(null);

  const cargarPlantillas = useCallback(async () => {
    setLoading(true);
    const data = await ocrPlantillasListar();
    setPlantillas(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    cargarPlantillas();
  }, [cargarPlantillas]);

  const handleNueva = () => {
    setPlantillaSeleccionada(null);
    setModalOpen(true);
  };

  const handleEditar = (row) => {
    setPlantillaSeleccionada(row);
    setModalOpen(true);
  };

  const handleEliminar = async (id) => {
    const ok = await ocrPlantillasEliminar(id);
    if (ok) cargarPlantillas();
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "nombre", headerName: "Nombre", flex: 1 },
    { field: "descripcion", headerName: "Descripción", flex: 1.2 },
    {
      field: "tipo",
      headerName: "Tipo",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value === "extraccion" ? "Extracción" : "Validación"}
          color={TIPO_COLOR[params.value] ?? "default"}
          size="small"
        />
      ),
    },
    {
      field: "umbral_confianza",
      headerName: "Umbral conf.",
      width: 120,
      renderCell: (params) => `${params.value ?? 80}%`,
    },
    {
      field: "campos_count",
      headerName: "Campos",
      width: 90,
      renderCell: (params) =>
        Array.isArray(params.row.campos) ? params.row.campos.length : 0,
    },
    {
      field: "cuentas_ids",
      headerName: "Clientes",
      flex: 1,
      renderCell: (params) => {
        const ids = params.value || [];
        return ids.length > 0
          ? <Chip label={`${ids.length} cliente${ids.length > 1 ? 's' : ''}`} size="small" color="info" variant="outlined" />
          : <Typography variant="caption" color="text.disabled">—</Typography>;
      },
    },
    {
      field: "acciones",
      headerName: "Acciones",
      type: "actions",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="Editar">
              <EditIcon color="primary" />
            </Tooltip>
          }
          onClick={() => handleEditar(params.row)}
          label="Editar"
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Eliminar">
              <DeleteOutlineIcon color="error" />
            </Tooltip>
          }
          onClick={() => handleEliminar(params.row.id)}
          label="Eliminar"
        />,
      ],
    },
  ];

  return (
    <Grid style={{ marginBottom: "50px", paddingTop: 10 }}>
      <PageHeaderCard
        title="OCR - Plantillas de Extracción"
        icon={<DocumentScannerIcon />}
      />

      <Container maxWidth="xxl" style={{ padding: "0px 0px 50px 0px" }}>
        <GlobalStyles styles={globalEstilos} />

        <StyledDataGrid
          rows={plantillas}
          columns={columns}
          loading={loading}
          density="standard"
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          disableRowSelectionOnClick
          autoHeight={plantillas.length > 0}
          components={{ Toolbar: () => <CustomToolbar onNueva={handleNueva} /> }}
          sx={{
            ...estiloGrilla,
            backgroundColor: theme.palette.background.paper,
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100]}
          pagination
        />
      </Container>

      {modalOpen && (
        <FormOcrPlantilla
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccess={cargarPlantillas}
          plantilla={plantillaSeleccionada}
        />
      )}
    </Grid>
  );
}
